import { useState } from 'react';
import { Calendar, X } from 'lucide-react';

function DateRangePicker({ startDate, endDate, onChange }) {
  const [isOpen, setIsOpen] = useState(false);

  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleStartDateChange = (e) => {
    const newStartDate = e.target.value ? new Date(e.target.value) : null;
    onChange({
      start: newStartDate,
      end: endDate
    });
  };

  const handleEndDateChange = (e) => {
    const newEndDate = e.target.value ? new Date(e.target.value) : null;
    onChange({
      start: startDate,
      end: newEndDate
    });
  };

  const clearDates = () => {
    onChange({
      start: null,
      end: null
    });
  };

  const formatDateForInput = (date) => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  const hasDateRange = startDate && endDate;

  return (
    <div className="space-y-3">
      {/* Date Range Display */}
      <div 
        className="flex items-center justify-between p-3 rounded-lg border cursor-pointer"
        style={{ 
          background: hasDateRange ? 'rgba(139, 69, 19, 0.05)' : 'var(--bg)',
          borderColor: hasDateRange ? 'var(--primary)' : 'var(--border)'
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4" style={{ color: 'var(--primary)' }} />
          <span className="text-sm" style={{ color: 'var(--text)' }}>
            {hasDateRange 
              ? `${formatDate(startDate)} - ${formatDate(endDate)}`
              : 'Select date range'
            }
          </span>
        </div>
        
        {hasDateRange && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              clearDates();
            }}
            className="p-1 rounded hover:bg-red-100"
            title="Clear date range"
          >
            <X className="w-3 h-3 text-red-500" />
          </button>
        )}
      </div>

      {/* Date Inputs */}
      {isOpen && (
        <div 
          className="p-4 rounded-lg border space-y-4"
          style={{ 
            background: 'var(--panel)',
            borderColor: 'var(--border)'
          }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Start Date */}
            <div>
              <label 
                className="block text-xs font-medium mb-2"
                style={{ color: 'var(--muted)' }}
              >
                Start Date
              </label>
              <input
                type="date"
                value={formatDateForInput(startDate)}
                onChange={handleStartDateChange}
                className="w-full p-2 rounded-md border text-sm"
                style={{ 
                  background: 'var(--bg)',
                  borderColor: 'var(--border)',
                  color: 'var(--text)'
                }}
              />
            </div>

            {/* End Date */}
            <div>
              <label 
                className="block text-xs font-medium mb-2"
                style={{ color: 'var(--muted)' }}
              >
                End Date
              </label>
              <input
                type="date"
                value={formatDateForInput(endDate)}
                onChange={handleEndDateChange}
                min={formatDateForInput(startDate)}
                className="w-full p-2 rounded-md border text-sm"
                style={{ 
                  background: 'var(--bg)',
                  borderColor: 'var(--border)',
                  color: 'var(--text)'
                }}
              />
            </div>
          </div>

          {/* Quick Presets */}
          <div>
            <div className="text-xs font-medium mb-2" style={{ color: 'var(--muted)' }}>
              Quick Select
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => {
                  const today = new Date();
                  const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                  onChange({ start: lastWeek, end: today });
                }}
                className="px-2 py-1 rounded text-xs border"
                style={{ 
                  borderColor: 'var(--border)',
                  color: 'var(--text)'
                }}
              >
                Last 7 days
              </button>
              <button
                onClick={() => {
                  const today = new Date();
                  const lastMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
                  onChange({ start: lastMonth, end: today });
                }}
                className="px-2 py-1 rounded text-xs border"
                style={{ 
                  borderColor: 'var(--border)',
                  color: 'var(--text)'
                }}
              >
                Last 30 days
              </button>
              <button
                onClick={() => {
                  const today = new Date();
                  const thisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                  onChange({ start: thisMonth, end: today });
                }}
                className="px-2 py-1 rounded text-xs border"
                style={{ 
                  borderColor: 'var(--border)',
                  color: 'var(--text)'
                }}
              >
                This month
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
            <button
              onClick={() => setIsOpen(false)}
              className="px-3 py-1 rounded text-sm"
              style={{ color: 'var(--muted)' }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DateRangePicker;